import { MessageFilled, MessageOutlined, WarningFilled } from '@ant-design/icons';
import { Badge, Button, ConfigProvider, Dropdown, Empty, MenuProps, Popover, Space, Typography, theme } from 'antd';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { startWSConnection } from '../../../clients/chat/connection';
import useAgentStatusOptions from '../../../hooks/agentStatus/useAgentStatusOptions';
import useToggle from '../../../hooks/useToggle';
import { agentStatusIdType, callPluginStatusMap } from '../../../model/types';
import { grispiNotification } from '../../../store/useNotificationStore';
import getStore, { getPluginStore } from '../../../utils/store/getStore';
import {
    getAgentCurrentPluginStatus,
    getCallPlugin,
    getChatPlugin,
    hasPluginTypeAnyErrors,
    isAnyPluginInitializing,
    sendAgentStatusChangeEventToPlugins
} from '../../../utils/store/pluginStore';
import { PluginType } from '../types';
import ChatMessagesUpdates from './ChatMessagesUpdates';
import ChatQueueTracker from './ChatQueueTracker';
import { CHAT_APP_UI_STATES, CHAT_APP_UI_STATES_SELECTABLE, ChatAppUIStates, chatAppStateColorMap } from './types';

const styles = {
    icon: { width: 10, height: 10, borderRadius: '50%' }
};

interface Props {
    pluginId?: string;
    chatAppStatus?: ChatAppUIStates;
}

export type chatAppStatusOptionType =
    (typeof CHAT_APP_UI_STATES_SELECTABLE)[keyof typeof CHAT_APP_UI_STATES_SELECTABLE];

export const agentStatusIdAsPerCallStatusMap: Record<string, Record<string, agentStatusIdType>> = {
    [CHAT_APP_UI_STATES.ONLINE]: {
        [callPluginStatusMap.ONLINE]: 10,
        [callPluginStatusMap.ONLINE_INBOUND]: 11,
        [callPluginStatusMap.ONLINE_OUTBOUND]: 12,
        [callPluginStatusMap.AFTER_CALL_OPS]: 13,
        [callPluginStatusMap.OFFLINE]: 9,
        [callPluginStatusMap.AWAY]: 9 // to set call plugin status to offline, should fire event
    },
    [CHAT_APP_UI_STATES.OFFLINE]: {
        [callPluginStatusMap.ONLINE]: 2,
        [callPluginStatusMap.ONLINE_INBOUND]: 3,
        [callPluginStatusMap.ONLINE_OUTBOUND]: 4,
        [callPluginStatusMap.AFTER_CALL_OPS]: 5,
        [callPluginStatusMap.OFFLINE]: 6,
        [callPluginStatusMap.AWAY]: 6 // to set call plugin status to offline, should fire event
    },
    [CHAT_APP_UI_STATES.AWAY]: {
        [callPluginStatusMap.ONLINE]: 2, // to set chat plugin status to offline
        [callPluginStatusMap.ONLINE_INBOUND]: 3, // to set chat plugin status to offline
        [callPluginStatusMap.ONLINE_OUTBOUND]: 4, // to set chat plugin status to offline
        [callPluginStatusMap.AFTER_CALL_OPS]: 5, // to set chat plugin status to offline
        [callPluginStatusMap.OFFLINE]: 6, // to set chat plugin status to offline
        [callPluginStatusMap.AWAY]: 7
    }
};

const shouldFireEventAgentChatStatuses: chatAppStatusOptionType[] = [
    CHAT_APP_UI_STATES.ONLINE,
    CHAT_APP_UI_STATES.OFFLINE
];

export const deriveAgentStatusIdAsPerAgentCurrentCallStatus = (
    selectedChatStatus: chatAppStatusOptionType
): agentStatusIdType => {
    if (
        !('pluginId' in getCallPlugin()) ||
        isAnyPluginInitializing(PluginType.CALL) ||
        hasPluginTypeAnyErrors(PluginType.CALL)
    ) {
        if (selectedChatStatus === 'AWAY')
            return agentStatusIdAsPerCallStatusMap[selectedChatStatus][callPluginStatusMap.AWAY];

        // agent not logged into alotech plugin, thus call plugin should be assumed as 'OFFLINE' to set currentAgentStatusId
        return agentStatusIdAsPerCallStatusMap[selectedChatStatus][callPluginStatusMap.OFFLINE];
    }

    const agentCurrentCallStatus = getAgentCurrentPluginStatus('call');

    const shouldFireEventToCallPlugin =
        agentCurrentCallStatus === 'AWAY' && shouldFireEventAgentChatStatuses.includes(selectedChatStatus);

    if (shouldFireEventToCallPlugin) {
        throw new Error(
            `Agent cannot have AWAY status for Call App for this option. Call App will be set to OFFLINE, explicitly`
        );
    }

    return agentStatusIdAsPerCallStatusMap[selectedChatStatus][agentCurrentCallStatus];
};

// This component is responsible to render the chatAppStatusButton, ChatQueueTracker, ChatMessagesUpdates component based on chatAppStatus prop
export default function ChatApp({
    // pluginId,
    chatAppStatus
}: Props) {
    const {
        token: { colorError, fontSizeHeading2 }
    } = theme.useToken();

    const { t } = useTranslation('common');
    const { data: agentStatusOptions } = useAgentStatusOptions();

    const [dropdownVisible, toggle] = useToggle();

    const onChangeStatus: MenuProps['onClick'] = (info) => {
        const { key: newStatus } = info;
        toggle();

        if (getAgentCurrentPluginStatus('chat') === newStatus) {
            // console.debug('Agent chat status is already set to this value');
            return;
        }
        try {
            const newAgentStatusId = deriveAgentStatusIdAsPerAgentCurrentCallStatus(
                newStatus as chatAppStatusOptionType
            );
            getPluginStore().changeAgentStatusOverMobXStore(newAgentStatusId);
        } catch (error) {
            // agent current call status is 'AWAY' and chat status is wanted to be set to 'OFFLINE' | 'ONLINE'. We want to set call status to be 'OFFLINE'
            grispiNotification('warning', error.message, 'warning');
            const selectedAgentStatus = agentStatusOptions?.find(
                (agentStatus) =>
                    agentStatus.id === agentStatusIdAsPerCallStatusMap[newStatus][callPluginStatusMap.OFFLINE]
            );

            // no need response of below function because we're sure that call plugin is available here and status change will be triggered over call plugin with dispatched status change event
            sendAgentStatusChangeEventToPlugins(selectedAgentStatus);
            // changeAgentStatusOverMobXStore function will be triggered after call plugin response is received
        }
    };

    useEffect(() => {
        if (chatAppStatus === 'initializingError' && !!agentStatusOptions) {
            try {
                const newAgentStatusId = deriveAgentStatusIdAsPerAgentCurrentCallStatus('OFFLINE');

                getPluginStore().changeAgentStatusOverMobXStore(newAgentStatusId);
            } catch (error) {
                // agent current call status is 'AWAY' and chat status is wanted to be set to 'OFFLINE. We want to set call status to be 'OFFLINE'
                grispiNotification('warning', error.message, 'warning');
                const selectedAgentStatus = agentStatusOptions?.find(
                    (agentStatus) =>
                        agentStatus.id ===
                        agentStatusIdAsPerCallStatusMap[CHAT_APP_UI_STATES.OFFLINE][getAgentCurrentPluginStatus('call')]
                );

                // no need response of below function because we're sure that call plugin is available here and status change will be triggered over call plugin with dispatched status change event
                sendAgentStatusChangeEventToPlugins(selectedAgentStatus);
                // changeAgentStatusOverMobXStore function will be triggered after call plugin response is received
            }
        }
    }, [chatAppStatus, agentStatusOptions]);

    if (chatAppStatus === 'initializingError')
        return (
            <Popover
                arrow={false}
                overlayStyle={{ width: 250 }}
                content={
                    <Empty
                        description={getStore()?.PluginStore?.chatPlugin?.name}
                        image={<MessageOutlined style={{ fontSize: fontSizeHeading2 }} />}
                        imageStyle={{ width: '100%', height: '100%' }}
                    >
                        <Trans
                            ns="common"
                            i18nKey="plugin.chat.errorWithReconnect"
                            values={{
                                status: t(`plugin.chat.status.${chatAppStatus}` as any),
                                error: null
                            }}
                        >
                            <strong>Error:</strong>
                            <span>status</span>
                            <p>error</p>
                            <p>message</p>
                            <p>or</p>
                            <Button
                                onClick={() => {
                                    // console.debug(`ChatApp, startWSConnection on re-connect request`);

                                    startWSConnection(getChatPlugin()?.manifest?.src);
                                }}
                            >
                                Re-connect
                            </Button>
                        </Trans>
                    </Empty>
                }
                placement="bottomRight"
                title={<Typography.Text strong>{t(`plugin.chat.status.${chatAppStatus}` as any)}</Typography.Text>}
                trigger="hover"
            >
                <Badge count={<WarningFilled style={{ color: colorError }} />} offset={[0, 30]}>
                    <Button type="primary" ghost disabled icon={<MessageOutlined />} />
                </Badge>
            </Popover>
        );

    const items: MenuProps['items'] = Object.keys(CHAT_APP_UI_STATES)
        .filter((status) => status !== 'initializingError')
        .map((status) => ({
            key: status,
            label: (
                <Space className="full-width" size="small">
                    <div style={{ ...styles.icon, backgroundColor: chatAppStateColorMap[status] }} />
                    {t(`plugin.chat.status.${status}` as any) as string}
                </Space>
            )
        }));

    const statusColor = chatAppStateColorMap?.[chatAppStatus];

    return (
        <Space>
            <Dropdown
                menu={{ items, onClick: onChangeStatus, selectedKeys: [chatAppStatus] }}
                onOpenChange={toggle}
                open={dropdownVisible}
                placement="bottom"
                trigger={['click']}
            >
                <ConfigProvider theme={{ token: { colorPrimary: statusColor, colorText: statusColor } }}>
                    <Button
                        type="primary"
                        ghost={!!chatAppStatus}
                        icon={<MessageFilled />}
                        onClick={toggle}
                        disabled={!chatAppStatus}
                    />
                </ConfigProvider>
            </Dropdown>

            <ChatQueueTracker
                isOnline={chatAppStatus === 'ONLINE'}
                isChatQueueTrackable={chatAppStatus !== 'OFFLINE'}
            />
            <ChatMessagesUpdates isOnline={chatAppStatus === 'ONLINE'} />
        </Space>
    );
}
